import React, {useEffect, useState} from 'react';
import {IUser} from "../../../../../app/interfaces/user/IUser";
import {useTranslation} from "react-i18next";
import {IError} from "../../../../../app/interfaces/response/IResponse";
import {
    ISelfBillingChangeCostGetAction
} from "../../../../../app/interfaces/selfBilling/ISelfBillingChangeCostGetAction";
import useSelfBillingApi from "../../../../../app/api/selfBilling";
import {isAxiosError} from "axios";
import {toast} from "react-toastify";
import {ResponseResultCode} from "../../../../../app/enums/ResponseResultCode";
import ToastLayout from "../../../../../components/uiLayouts/ToastLayout/ToastLayout";
import FormLoadingScreen from "../../../../../components/ui/loaders/FormLoadingScreen/FormLoadingScreen";
import {
    ISelfBillingChangeCostPostAction
} from "../../../../../app/interfaces/selfBilling/ISelfBillingChangeCostPostAction";
import {BLErrorCode} from "../../../../../app/enums/BLErrorCode";
import Alert from "../../../../../components/ui/Alert/Alert";
import Button, {ButtonType} from "../../../../../components/ui/Button/Button";
import ChangeCostForm from "./ChangeCostForm/ChangeCostForm";

type ChangeCostProps = {
    shipmentGid: string;
    user?: IUser;
    close?: () => void;
};

const alertStyle: React.CSSProperties = {
    margin: '5px 10px',
    fontSize: '11px',
    fontWeight: 'bold',
}

const ChangeCost: React.FC<ChangeCostProps> = ({
                                                   shipmentGid,
                                                   user,
                                                   close
                                               }) => {
    const {t} = useTranslation();
    const [model, setModel] = useState<ISelfBillingChangeCostGetAction | null>(null);
    const [disabled, setDisabled] = useState<boolean>(false);
    const [errors, setErrors] = useState<Array<IError>>([]);

    const {
        getSelfBillingChangeCostData: {
            isLoading: getSelfBillingChangeCostDataIsLoading,
            cancel: cancelGetSelfBillingChangeCostData,
            query: getSelfBillingChangeCostData
        },
        getSelfBillingChangeCostDataAnonymous: {
            isLoading: getSelfBillingChangeCostDataAnonymousIsLoading,
            cancel: cancelGetSelfBillingChangeCostDataAnonymous,
            query: getSelfBillingChangeCostDataAnonymous
        },
        changeCostSelfBillingData: {
            mutation: changeCostSelfBillingData,
            cancel: cancelChangeCostSelfBillingData
        },
        changeCostSelfBillingDataAnonymous: {
            mutation: changeCostSelfBillingDataAnonymous,
            cancel: cancelChangeCostSelfBillingDataAnonymous
        }
    } = useSelfBillingApi();

    useEffect(() => {
        if (!shipmentGid || shipmentGid === '') {
            if (close) {
                close();
            }

            return;
        }

        (async () => {
            try {
                const response = user
                    ? await getSelfBillingChangeCostData(shipmentGid)
                    : await getSelfBillingChangeCostDataAnonymous(shipmentGid);

                if (response?.status === 200 && response.data && response.data.data && response.data.data) {
                    setModel(response.data.data);
                }
            } catch (e) {
                if (isAxiosError(e)) {
                    toast.error<string>(`Unable to get change cost data. Error: ${e.message}`);

                    if (close) {
                        close();
                    }
                }
            }
        })();

        return () => {
            cancelGetSelfBillingChangeCostData();
            cancelGetSelfBillingChangeCostDataAnonymous();
            cancelChangeCostSelfBillingData();
            cancelChangeCostSelfBillingDataAnonymous();
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shipmentGid]);

    const save = async (values: ISelfBillingChangeCostPostAction): Promise<void> => {
        if (!model) {
            return;
        }

        setDisabled(prev => !prev);

        try {
            let data: FormData = new FormData();

            let isNewPaymentData: boolean = false;

            if (values.mode === 'OwnBankAccount' &&
                values.bankAccountModel &&
                values.bankAccountModel.description &&
                values.bankAccountModel.description === 'ADDED') {
                isNewPaymentData = true;

                data.append('newBankAccount', values.bankAccountModel.value);
                data.append('isNewBankAccount', 'true');
                data.append('isNoInList', 'false');
                data.append('isOtherPayer', 'false');
                data.append('isBankAccount', 'false');
            }

            if (values.mode === 'NewRequisites' && values.financialFile) {
                isNewPaymentData = true;

                data.append('isNoInList', 'true');
                data.append('isBankAccount', 'false');
                data.append('isOtherPayer', 'false');
                data.append('bankAccountNew', 'false');
            }


            if (!isNewPaymentData) {
                if (values.mode === 'OwnBankAccount' && values.bankAccountModel) {
                    data.append('bankAccount', values.bankAccountModel.value);
                    data.append('isBankAccount', 'true');
                    data.append('isNoInList', 'false');
                    data.append('isOtherPayer', 'false');
                    data.append('bankAccountNew', 'false');
                }

                if (values.mode === 'AnotherPaymentRecipients' && values.anotherPaymentRecipientModel) {
                    data.append('anotherPaymentRecipient', values.anotherPaymentRecipientModel.value);
                    data.append('isOtherPayer', 'true');
                    data.append('isNoInList', 'false');
                    data.append('isBankAccount', 'false');
                    data.append('bankAccountNew', 'false');
                }

                if (values.mode === 'NewRequisites' && values.financialFile) {
                    data.append('isNoInList', 'true');
                    data.append('isBankAccount', 'false');
                    data.append('isOtherPayer', 'false');
                    data.append('bankAccountNew', 'false');
                }
            }

            data.append('invoiceNumber', values.invoiceNumber ?? '');
            data.append('category', values.categoryModel.value);
            data.append('costShipmentGid', model.costShipmentGid);
            data.append('notes', values.notes ?? '');
            data.append('orderReleaseGid', values.orderReleaseGid);
            data.append('shipmentGid', values.shipmentGid);
            data.append('revertVat', model.revertVat ? 'true' : 'false');

            if (values.bet) {
                data.append('bet', values.bet);
            }

            if (values.currency) {
                data.append('currency', values.currency);
            }

            if (values.isIncorrectVatNotesShown && values.incorrectVatNotes !== null && values.incorrectVatNotes !== '') {
                data.append('incorrectVatNotes', values.incorrectVatNotes);
            }

            if (!values.cmrFile) {
                return;
            } else {
                data.append('file', values.cmrFile);
            }

            if (values.financialFile) {
                data.append('financialFile', values.financialFile);
            }

            const response = user
                ? await changeCostSelfBillingData(data)
                : await changeCostSelfBillingDataAnonymous(data);

            if (response?.status === 200 && response.data?.resultCode === ResponseResultCode.Ok) {
                toast.success(<ToastLayout text={t("shared.labels.delayed-update-warning")}
                                           title={`${t("self-billing.btn.change-cost")}`}/>);

                if (close) {
                    close();
                }
            }
        } catch (err) {
            if (isAxiosError(err)) {
                if (err.response && err.response.data && err.response.status === 422 &&
                    err.response.data.resultCode === ResponseResultCode.NotValidData &&
                    err.response.data.errors && Array.isArray(err.response.data.errors)) {
                    setErrors(err.response.data.errors);
                } else if (close) {
                    close();

                    toast.error(`Unable to change cost. Error: ${err.message}`);
                }
            }
        }

        setDisabled(prev => !prev);
    }

    if (!shipmentGid || shipmentGid === '') {
        return null;
    }

    if (getSelfBillingChangeCostDataIsLoading || getSelfBillingChangeCostDataAnonymousIsLoading || model === null) {
        return (
            <FormLoadingScreen height={`350px`} style={{padding: '5px'}}/>
        );
    }

    return (
        <div className={'h100 w100'}>
            <div className={'dialog-default-content-container'}>
                {errors.map(item => {
                    switch (item.errorCode) {
                        case BLErrorCode.InnerError:
                            return (
                                <Alert key={item.errorCode} type={'Error'} style={alertStyle}>
                                    {item.description}
                                </Alert>
                            );
                        case BLErrorCode.CmrNotSupportedFormat:
                            return (
                                <Alert key={item.errorCode} type={'Error'} style={alertStyle}>
                                    {t("self-billing.errors.cmr-not-supported-format")}
                                </Alert>
                            );
                        case BLErrorCode.CmrFileIsBig:
                            return (
                                <Alert key={item.errorCode} type={'Error'} style={alertStyle}>
                                    {t("self-billing.errors.cmr-file-is-big")}
                                </Alert>
                            );
                        case BLErrorCode.CmrFileIsEmpty:
                            return (
                                <Alert key={item.errorCode} type={'Error'} style={alertStyle}>
                                    {t("self-billing.errors.cmr-file-is-empty")}
                                </Alert>
                            );
                        case BLErrorCode.FinFileIsEmpty:
                            return (
                                <Alert key={item.errorCode} type={'Error'} style={alertStyle}>
                                    {t("self-billing.errors.fin-file-is-empty")}
                                </Alert>
                            );
                        case BLErrorCode.CmrHasInvoice:

                            return (
                                <Alert key={item.errorCode} type={'Error'} style={alertStyle}>
                                    {t("self-billing.errors.cmr-has-invoice").replace('{0}', item.description ?? '')}
                                </Alert>
                            );
                    }

                    return null;
                })}

                <ChangeCostForm model={model}
                                user={user}
                                disabled={disabled}
                                onSubmit={save}/>
            </div>
            <div className={'dialog-default-buttons-container'}>
                {close &&
                    <Button buttonType={ButtonType.Transparent}
                            disabled={disabled}
                            className={'close-button'}
                            onClick={close}>
                        {t("shared.btn.cancel")}
                    </Button>
                }
                <Button buttonType={ButtonType.Primary}
                        disabled={disabled}
                        form='change-cost-form'
                        type='submit'
                        style={{minWidth: '150px'}}
                        className={'confirm-button'}
                        isLoading={disabled}>
                    {t("shared.btn.confirm")}
                </Button>
            </div>
        </div>
    );
};

export default ChangeCost;